import React, { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { toCents } from "@/lib/format";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Loader2, ArrowDownCircle, ArrowUpCircle } from "lucide-react";

const input = "w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20";

// type: "CASH_DEPOSIT" | "CASH_WITHDRAWAL"
export default function CashMovementDialog({ open, onOpenChange, sessionId, type, currency }) {
  const { orgId } = useAuth();
  const qc = useQueryClient();
  const [amount, setAmount] = useState("");
  const [reason, setReason] = useState("");
  const isDeposit = type === "CASH_DEPOSIT";

  const mut = useMutation({
    mutationFn: async () => (await api.post(`/api/cash/${sessionId}/movements`,
      { type, amount_cents: toCents(amount), reason }, { params: { organization_id: orgId } })).data,
    onSuccess: () => { toast.success(isDeposit ? "Dépôt enregistré" : "Retrait enregistré"); qc.invalidateQueries(); onOpenChange(false); setAmount(""); setReason(""); },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail) || e.message),
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="cash-movement-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {isDeposit ? <ArrowDownCircle className="h-5 w-5 text-emerald-600" /> : <ArrowUpCircle className="h-5 w-5 text-rose-600" />}
            {isDeposit ? "Dépôt d'espèces" : "Retrait d'espèces"}
          </DialogTitle>
          <DialogDescription className="sr-only">Enregistrez un mouvement physique d'espèces dans la caisse.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <label className="mb-1 block text-sm font-medium text-slate-700">Montant ({currency})</label>
            <input type="number" min="0" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="1000" data-testid="movement-amount-input" className={input} autoFocus />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-slate-700">Motif</label>
            <input value={reason} onChange={(e) => setReason(e.target.value)} placeholder={isDeposit ? "Ajout monnaie" : "Remise au dirigeant"} data-testid="movement-reason-input" className={input} />
          </div>
          <p className="text-xs text-slate-400">Un mouvement d'espèces n'est ni une recette, ni une dépense — il ajuste uniquement la caisse physique.</p>
          <button disabled={mut.isPending || amount === "" || !reason.trim()} onClick={() => mut.mutate()} data-testid="confirm-movement-button"
            className="flex w-full items-center justify-center gap-2 rounded-lg bg-primary px-4 py-2.5 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-50">
            {mut.isPending && <Loader2 className="h-4 w-4 animate-spin" />} Enregistrer
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
